/**
 * Parses agent message strings for Shopify order blocks.
 *
 * Supported block format:
 *   [ORDER_CARD:{...json...}]
 *
 * The JSON payload contains nested arrays/objects (e.g. line_items),
 * so a flat regex like `\[ORDER_CARD:(\{[^[\]]*\})\]` rejects it.
 * We use a brace-balanced parser instead — same idea as a tiny
 * JSON-find: track quote state, count `{`/`}` depth, require the
 * trailing `]` after depth returns to zero.
 *
 * Tolerates JSON parse failures by returning the original marker
 * text verbatim, never throws.
 */

export interface OrderLineItem {
  title: string;
  qty: number;
  price: string;
  image_url?: string | null;
}

export interface OrderCardData {
  order_id: string;
  number: string;
  financial_status: 'paid' | 'pending' | 'refunded' | 'partially_refunded' | string;
  fulfillment_status: 'unfulfilled' | 'partial' | 'fulfilled' | string;
  total: string;
  currency: string;
  created_at: string;
  line_items: OrderLineItem[];
  order_status_url?: string;
  // Optional alphanumeric codes — rendered visually so they aren't
  // spoken/garbled in voice. Absent on Shopify + existing cards.
  tracking?: string;      // tracking number (monospace, copyable)
  tracking_url?: string;  // when present, `tracking` links here
  code?: string;          // generic confirmation/reference/promo code
}

export type OrderSegment =
  | { type: 'text'; content: string }
  | { type: 'order_card'; data: OrderCardData };

interface OrderCardMarker {
  start: number;   // index of the leading '['
  end: number;     // index AFTER the trailing ']'
  jsonStr: string; // the `{...}` payload, ready for JSON.parse
}

const PREFIX = '[ORDER_CARD:';

function findOrderCardMarkers(text: string): OrderCardMarker[] {
  const out: OrderCardMarker[] = [];
  let pos = 0;

  while (true) {
    const idx = text.indexOf(PREFIX, pos);
    if (idx === -1) break;
    const jsonStart = idx + PREFIX.length;
    if (text[jsonStart] !== '{') {
      pos = jsonStart;
      continue;
    }

    let depth = 0;
    let inString = false;
    let escape = false;
    let i = jsonStart;
    let jsonEnd = -1;
    for (; i < text.length; i++) {
      const c = text[i];
      if (escape) { escape = false; continue; }
      if (c === '\\') { escape = true; continue; }
      if (c === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (c === '{') depth++;
      else if (c === '}') {
        depth--;
        if (depth === 0) { jsonEnd = i + 1; break; }
      }
    }
    if (jsonEnd === -1) { pos = i; continue; }              // unbalanced — skip
    if (text[jsonEnd] !== ']') { pos = jsonEnd; continue; } // missing closing bracket

    out.push({
      start: idx,
      end: jsonEnd + 1,
      jsonStr: text.substring(jsonStart, jsonEnd),
    });
    pos = jsonEnd + 1;
  }

  return out;
}

export function parseOrderBlocks(message: string): {
  cleanText: string;
  cards: OrderCardData[];
} {
  const cards: OrderCardData[] = [];
  let cleanText = '';
  let lastIndex = 0;

  for (const marker of findOrderCardMarkers(message)) {
    if (marker.start > lastIndex) {
      cleanText += message.slice(lastIndex, marker.start);
    }
    try {
      const data = JSON.parse(marker.jsonStr) as OrderCardData;
      cards.push(data);
    } catch {
      cleanText += message.substring(marker.start, marker.end);
    }
    lastIndex = marker.end;
  }

  if (lastIndex < message.length) {
    cleanText += message.slice(lastIndex);
  }

  if (cards.length === 0 && cleanText === '') {
    cleanText = message;
  }

  return { cleanText, cards };
}

/**
 * Segment-style parser for interleaved rendering — preserves the
 * original order of text vs. cards within a single message bubble,
 * same shape as parseProductBlocks.
 */
export function parseOrderSegments(message: string): OrderSegment[] {
  const segments: OrderSegment[] = [];
  let lastIndex = 0;

  for (const marker of findOrderCardMarkers(message)) {
    if (marker.start > lastIndex) {
      segments.push({ type: 'text', content: message.slice(lastIndex, marker.start) });
    }
    try {
      const data = JSON.parse(marker.jsonStr) as OrderCardData;
      segments.push({ type: 'order_card', data });
    } catch {
      segments.push({ type: 'text', content: message.substring(marker.start, marker.end) });
    }
    lastIndex = marker.end;
  }

  if (lastIndex < message.length) {
    segments.push({ type: 'text', content: message.slice(lastIndex) });
  }

  if (segments.length === 0) {
    return [{ type: 'text', content: message }];
  }

  return segments;
}
